import type { GetResult, Models } from "api/types";
import { formatCurrency } from "toolbox/Money";

export const getProductTitle = (
  productName: string | null | undefined,
  variantName: string | null | undefined
) => {
  if (!productName && !variantName) {
    return "";
  }

  return productName &&
    variantName &&
    productName.toLocaleLowerCase() != variantName.toLocaleLowerCase()
    ? productName + " - " + variantName
    : variantName ?? "";
};

export const checkIfListingsHaveConcerns = (
  listings: GetResult<"/listings"> | undefined
) => {
  return !!listings?.some(({ concernIds }) => concernIds.length);
};

export const getMembershipRewardGroupSubheader = (
  billingPeriodsBeforeFirstReward: number | null | undefined,
  billingPeriodInterval: number | null | undefined,
  membershipBillingPeriod?: string | null
) => {
  if (
    billingPeriodsBeforeFirstReward === null ||
    billingPeriodsBeforeFirstReward === undefined
  ) {
    return "";
  }

  const timePeriod =
    billingPeriodsBeforeFirstReward === 0 ? "day" : membershipBillingPeriod;
  const dayNumberWithOrdinal = getNumberWithOrdinal(
    billingPeriodsBeforeFirstReward === 0
      ? 1
      : billingPeriodsBeforeFirstReward * (billingPeriodInterval ?? 1)
  );

  return dayNumberWithOrdinal + " " + timePeriod;
};

export const getNumberWithOrdinal = (n: number | null | undefined) => {
  if (n === null || n === undefined) {
    return "";
  }

  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const getProductTitleQuantity = (
  quantity: number | null | undefined,
  packageUnitType: Models<"PackageUnitTypeModel"> | null | undefined,
  removeParenthesis?: boolean
) => {
  if (!quantity || !packageUnitType) {
    return "";
  }

  if (removeParenthesis && quantity > 1)
    return `${quantity} ${packageUnitType.pluralUnitType}`;

  return quantity > 1 ? `(${quantity} ${packageUnitType.pluralUnitType})` : "";
};

export const getBookAppointmentAction = (
  url?: string | null | undefined,
  phoneNumber?: string | null | undefined,
  email?: string | null | undefined
) => {
  if (!url && !phoneNumber && !email) {
    return "";
  }

  return (
    url || (phoneNumber && `tel:${phoneNumber}`) || (email && `mailto:${email}`)
  );
};

export const getPDPBannerDiscountText = (
  discount: Models<"ShopProductDiscountModel">,
  type:
    | "Membership"
    | "Package"
    | "Treatment"
    | "Product"
    | "ProductFromExternalSource",
  listings: Models<"ListingModel">[] = [],
  giftCards?: Models<"DisplayGiftCardListing">[]
) => {
  if (!discount) {
    return "";
  }

  const isBuyXGetY = discount.isBuyXGetY ?? false;
  if (isBuyXGetY)
    return getBXGYDiscountText(
      discount.discount ?? undefined,
      listings,
      giftCards
    );

  const { maxPercentageOff, maxAmountOff, title, isVariable } = discount;

  let discountAmount = "";
  if (maxPercentageOff) {
    discountAmount = `${Math.round(maxPercentageOff * 100)}%`;
  } else if (maxAmountOff) {
    discountAmount = `${formatCurrency(maxAmountOff)} off`;
  } else {
    return "";
  }

  const offerName = title ?? "this offer";

  if (type === "Membership") {
    return `Save ${discountAmount} on your first month of membership when you apply '${offerName}' in cart!`;
  }

  return `Save ${
    isVariable ? "up to " : ""
  }${discountAmount} on this item when you apply '${offerName}' in cart!`;
};

export const hasBeautyBankAvailable = (
  rewardGroup: Models<"MembershipRewardGroupModel">
) => {
  if (!rewardGroup) {
    return false;
  }

  return (rewardGroup.beautyBank?.creditAmount ?? 0) > 0;
};

export const getBXGYDiscountText = (
  discount?: Models<"DiscountModel">,
  listings?: Models<"ListingModel">[],
  giftCards?: Models<"DisplayGiftCardListing">[]
) => {
  if (
    !discount ||
    !listings?.length ||
    !discount.requirements ||
    !discount.inclusions
  )
    return "";

  const { requirements, inclusions } = discount;

  const inclusion = inclusions[0];
  const { quantityLimit } = inclusion;
  const percentOff = (discount.percentOff * 100).toFixed(0);

  const inclusionPackageName = findPackageName(inclusion.packageId, listings);
  const inclusionGiftCardName = findGiftCardName(
    inclusion.packageId,
    giftCards
  );
  const inclusionName = inclusionPackageName || inclusionGiftCardName;

  // Handle multiple requirements (e.g., multiple gift cards)
  const giftCardPrices: number[] = [];
  const packageNames: string[] = [];
  let minimumQuantity = 0;
  let minimumSpendAmount = 0;

  requirements.forEach((req) => {
    const packageName = findPackageName(req.packageId, listings);
    if (packageName) {
      packageNames.push(packageName);
    } else {
      const giftCardPrice = findGiftCardPrice(req.packageId, giftCards);
      if (giftCardPrice) {
        giftCardPrices.push(giftCardPrice);
      }
    }
    minimumQuantity = req.minimumQuantity ?? minimumQuantity;
    minimumSpendAmount = req.minimumSpendAmount ?? minimumSpendAmount;
  });

  let requirementName = "";
  if (packageNames.length > 0) {
    requirementName = packageNames.join(" or ");
  } else if (giftCardPrices.length > 0) {
    const uniquePrices = [...new Set(giftCardPrices)];
    const sortedPrices = uniquePrices.sort((a, b) => a - b);
    const formattedPrices = sortedPrices.map((price) =>
      formatCurrency(price, 0)
    );
    const hasMultiple = formattedPrices.length > 1;
    requirementName = hasMultiple
      ? `the following Gift Cards: ${formattedPrices.join(", ")}`
      : `the following Gift Card: ${formattedPrices[0]}`;
  }

  const requirementAction = minimumQuantity
    ? `Purchase ${minimumQuantity} of`
    : `Spend ${formatCurrency(minimumSpendAmount ?? 0, 0)} on`;

  const discountAction =
    (discount.percentOff === 1 && "as a complimentary gift.") ||
    (discount.amountOff &&
      `at ${formatCurrency(discount.amountOff, 0)} off.`) ||
    `at ${percentOff}% off.`;

  return `${requirementAction} ${requirementName}, and receive ${quantityLimit} of ${inclusionName} ${discountAction}`;
};

export const findPackageName = (
  packageId: string | null | undefined,
  listings: Models<"ListingModel">[] | undefined
) => {
  if (!packageId || !listings?.length) return "";
  const listing = listings.find((listing) =>
    listing.packages.some((pkg: Models<"PackageModel">) => pkg.id === packageId)
  );
  return (
    listing?.packages.find(
      (pkg: Models<"PackageModel">) => pkg.id === packageId
    )?.name ?? ""
  );
};

export const findGiftCardPrice = (
  packageId: string | null | undefined,
  giftCards?: Models<"DisplayGiftCardListing">[]
): number | null => {
  if (!packageId || !giftCards?.length) return null;

  const giftCard = giftCards.find((giftCard) =>
    giftCard.giftCards.some((gc) => gc.packageId === packageId)
  );

  if (!giftCard?.giftCards.length) return null;

  const specificGiftCard = giftCard.giftCards.find(
    (gc) => gc.packageId === packageId
  );

  return specificGiftCard?.baseCostInCents ?? null;
};

export const findGiftCardName = (
  packageId: string | null | undefined,
  giftCards?: Models<"DisplayGiftCardListing">[]
) => {
  if (!packageId || !giftCards?.length) return "";

  const price = findGiftCardPrice(packageId, giftCards);

  if (!price) return "";

  return `the following Gift Card: ${formatCurrency(price, 0)}`;
};
export const isBuyXGetYDiscount = (
  discount: Models<"DiscountModel"> | null | undefined
) => {
  if (!discount) return false;
  return !!(
    discount.inclusions?.length == 1 && (discount.requirements?.length ?? 0) > 0
  );
};
